// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, Subject } from 'rxjs';

// --- 3rd Party
import { cloneDeep } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { AddGroupCommand, AddRespondentCommand, CommandOfT, Constants, DeleteGroupCommand, DeleteRespondentCommand, GroupModel, MoveRespondentCommand, RespondentEmailValidationModel, RespondentModel, RespondentStatuses, UndoModel, UpdateGroupCommand } from '../../../models';
import { ParticipantGroupsBaseService } from '../survey/participant-groups-base.service';
import { RespondentsBaseService } from '../survey/respondents-base.service';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class ManageRespondentsService {

  private _changes: CommandOfT<GroupModel[]>[] = [];
  private _groupSnapshot: GroupModel[];

  private _alerts$: Subject<string> = new Subject<string>();
  get alerts$(): Observable<string> {
    return this._alerts$.asObservable();
  }

  private _undo$: BehaviorSubject<UndoModel | null> = new BehaviorSubject<UndoModel | null>(null);
  get undo$(): Observable<UndoModel | null> {
    return this._undo$.asObservable();
  }

  protected _participantGroups$: BehaviorSubject<GroupModel[]>;
  get participantGroups$(): Observable<GroupModel[]> {
    return this._participantGroups$.asObservable();
  }

  private _hasChanges$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get hasChanges$(): Observable<boolean> {
    return this._hasChanges$.asObservable();
  }

  constructor(
    private _groupsService: ParticipantGroupsBaseService,
    private _oidcService: OidcService,
    private _respondentsService: RespondentsBaseService,
    private _translateService: TranslateService
  ) {
    this._groupSnapshot = [] as GroupModel[];
    this._participantGroups$ = new BehaviorSubject<GroupModel[]>(this._groupSnapshot);
  }

  createSnapshot(): void {
    const snapshotSubscription = this._groupsService.participantGroups$.subscribe((groups) => {
      this._groupSnapshot = groups;
      this._participantGroups$.next(structuredClone(this._groupSnapshot));
      snapshotSubscription.unsubscribe();
    });
  }

  clearSnapshot(): void {
    this._groupSnapshot = [] as GroupModel[];
    this._participantGroups$.next(this._groupSnapshot);
    this._changes = [];
  }

  clearUndo(): void {
    this._undo$.next(null);
  }

  createGroup(id: string, numberOfRespondentsPerGroup: number): GroupModel | null {
    return this._groupsService.createGroup(id, numberOfRespondentsPerGroup);
  }

  addGroup(group: GroupModel): void {
    const groups = this._participantGroups$.value;
    const command = new AddGroupCommand(group, groups);
    this._execute(command);
  }

  addRespondent(respondent: RespondentModel) {
    const groups = this._participantGroups$.value;
    const command = new AddRespondentCommand(respondent, groups);
    this._execute(command);
  }

  validateRespondentEmailAddress(model: RespondentEmailValidationModel): void {
    const identitySubs = this._oidcService.claims$.subscribe(claims => {
      model.isValid = true;

      const participantEmailAddress: string = claims.getClaimValue(Constants.CLAIMS_PREFERRED_USERNAME)?.trim();

      const allRespondents: RespondentModel[] = this._participantGroups$.value.flatMap(g => g.respondents);
      const found = allRespondents.find(
        (r: RespondentModel) =>
          r.emailAddress.toLocaleLowerCase().trim() === model.emailAddress.toLocaleLowerCase().trim() &&
          (r.groupId !== model.groupId ||
            r.groupIndex !== model.respondentGroupIndex)
      );
      if (found) {
        model.isValid = false;
        model.errorMessage = this._translateService.instant(
          'respondentErrorMessageAlreadyARespondent'
        );
      } else {
        if (participantEmailAddress) {
          const emailAddress = participantEmailAddress.toLocaleLowerCase().trim();
          if (model.emailAddress.toLocaleLowerCase().trim() === emailAddress) {
            model.isValid = false;
            model.errorMessage = this._translateService.instant(
              'respondentErrorMessageRespondentIsUser'
            );
          }
        }
      }

      this._respondentsService.validateRespondentEmailAddressV2(model);
    });
  }

  deleteGroup(group: GroupModel) {
    if (group.respondents.some(r => r.status >= RespondentStatuses.started)) {
      this._alerts$.next(this._translateService.instant('v2RespondentGroupDeleteWhenRespondentsStartedError'));
    } else {
      const groups = this._participantGroups$.value;
      const command = new DeleteGroupCommand(group, groups);
      this._execute(command);

      this._undo$.next({
        text: this._translateService.instant('v2GroupDeleteUndotext', {groupName: group.groupName})
      } as UndoModel);
    }
  }

  deleteRespondent(respondent: RespondentModel): void {    
    if ((respondent.status >= RespondentStatuses.started)&&(respondent.status != RespondentStatuses.declined)) return;
    
    const groups = this._participantGroups$.value;
    const command = new DeleteRespondentCommand(respondent, groups);
    this._execute(command);

    this._undo$.next({
        text: this._translateService.instant('v2RespondentDeleteUndotext', { firstName: respondent.firstName, lastName: respondent.lastName})
      } as UndoModel
    );
  }

  moveRespondent(respondent: RespondentModel, target: GroupModel): void {
    
    const groups = this._participantGroups$.value;
    const command = new MoveRespondentCommand(respondent, target, groups);
    this._execute(command);

    this._undo$.next({
      text: this._translateService.instant('v2RespondentMoveUndotext', { firstName: respondent.firstName, lastName: respondent.lastName, newGroupName: target.groupName})
    } as UndoModel);
  }

  refreshGroups(participantId: string): void {
    this._groupsService.loadGroups(participantId);
  }

  updateGroup(group: GroupModel) {
    const groups = this._participantGroups$.value;
    const command = new UpdateGroupCommand(group, groups);
    this._execute(command);
  }

  undo(): void {
    const command = this._changes.pop();
    if (!command) return;
    const groups = cloneDeep(command.undo() as GroupModel[]);
    this._participantGroups$.next(groups);
    this.clearUndo();
    this._hasChanges$.next(this._changes.length > 0);
  }

  saveChanges(participantId: string) {
    const deletedGroups = this._groupSnapshot.filter(g => {
      const matched = this._participantGroups$.value.find(pg => pg.id.toLowerCase() === g.id.toLowerCase());
      return !!!matched;
    });

    const newGroups = this._participantGroups$.value.filter(pg => {
      const matched = this._groupSnapshot.find(g => g.id.toLowerCase() === pg.id.toLowerCase());
      return !!!matched;
    });
    newGroups.forEach(ng => {
      ng.respondents = ng.respondents.filter(r => r.emailAddress?.length > 0);
    });

    const changedGroups = this._participantGroups$.value.filter(pg => {
      const originalGroup = this._groupSnapshot.find(g => g.id.toLowerCase() === pg.id.toLowerCase());
      if (!originalGroup) return false;

      const groupChanged = (pg.groupName !== originalGroup?.groupName) || (pg.index !== originalGroup.index);
      let respondentsChanged = originalGroup.respondents.length !== pg.respondents.length ||
        originalGroup.respondents.some((r, index) => {
          const areDifferent = r.groupIndex !== pg.respondents[index].groupIndex || r.emailAddress !== pg.respondents[index].emailAddress;
          return areDifferent;
        });

      return groupChanged || respondentsChanged;
    });
    changedGroups.forEach(cg => {
      cg.respondents = cg.respondents.filter(r => r.emailAddress?.length > 0);
    });

    this._groupsService.saveGroupChanges(deletedGroups, newGroups, changedGroups, this._participantGroups$.value, participantId);
    this._changes = [];
    this._hasChanges$.next(this._changes.length > 0);
  }

  private _execute(command: CommandOfT<GroupModel[]>): void {
    const groups = command.execute();
    this._changes.push(command);
    const newGroups = structuredClone(groups);
    this._participantGroups$.next(newGroups);
    this._hasChanges$.next(this._changes.length > 0);
  }
}
